import { Entity, FieldType, makeClone, ReferenceField } from 'gossamer-unified';
import { User } from './User';
import { Person } from './Person';
import { InstitutionMember } from './InstitutionMember';
import {
  USERS_I_CAN_SEE_USER,
  USERS_I_CAN_SEE_ADMIN,
  USERS_I_CAN_UPDATE_ADMIN,
  USERS_I_CAN_UPDATE_USER,
} from './SecurityRules';

export interface UserViewRow {
  id: string;
  email: string;
  created_at: string;
  person_id: number;
  first_name: string;
  last_name: string;
  phone_number: string;
  photo: string | null;
  date_of_birth: string | null;
  occupation: string | null;
  address_id: number | null;
  specialty: string | null;
  employment_grade: string | null;
  year_group: string | null;
  receive_event_reminders: string;
  receive_unread_msg_emails: boolean;
  skip_all_email_sending: boolean;
  privacy_policy_accepted_version: number | null;
  platform_terms_accepted_version: number | null;
  booking_cancellation_refund_terms_accepted_version: number | null;
  host_payments_payouts_terms_accepted_version: number | null;
  unsuccessful_login_attempts: number | null;
  stripe_connect_account_id: string | null;
  stripe_connect_details_submitted: boolean;
  stripe_connect_charges_enabled: boolean;
  stripe_connect_payouts_enabled: boolean;
  stripe_connect_onboarded_at: string | null;
  pwa_status: string;
  pwa_installed_at: string | null;
}

const clone = makeClone();

// access conditions

export const UserView: Entity<UserViewRow> = {
  id: 'UserView',
  title: 'User',
  service: 'main',
  rowLabel: (row: UserViewRow) => [row.first_name, row.last_name].join(' '),
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.user_view',
  },
  security: [
    { role: 'user', action: 'read', access: 'conditional', condition: 'id IN ' + USERS_I_CAN_SEE_USER },
    { role: 'user', action: 'all', access: 'conditional', condition: 'id IN ' + USERS_I_CAN_UPDATE_USER },
    { role: 'admin', action: 'read', access: 'conditional', condition: 'id IN ' + USERS_I_CAN_SEE_ADMIN },
    { role: 'admin', action: 'all', access: 'conditional', condition: 'id IN ' + USERS_I_CAN_UPDATE_ADMIN },
    { role: 'staff', action: 'all', access: 'all' },
  ],
  defaultSort: [['email', 'ASC']],
  defaultFilters: [{ fieldId: 'email', operator: 'ct', value: '' }],
  listRowLink: '/admin/user/display/{key}',
  refOrderClause: 'SELECT ZZ.last_name FROM public.user_view ZZ WHERE ZZ.id',
  fields: {
    id: clone(User.fields.id, { seq: 5, listColumn: false }),
    first_name: clone(Person.fields.first_name, { seq: 10, listColumn: true, fieldGroup: 'display' }),
    last_name: clone(Person.fields.last_name, { seq: 15, listColumn: true, fieldGroup: 'display' }),
    email: clone(User.fields.email, { seq: 20, listColumn: true, fieldGroup: 'display' }),
    phone_number: clone(Person.fields.phone_number, { seq: 25, listColumn: false, fieldGroup: 'display' }),
    photo: clone(Person.fields.photo, { seq: 30, listColumn: false }),
    date_of_birth: clone(Person.fields.date_of_birth, { seq: 35, listColumn: false, fieldGroup: 'display' }),
    occupation: clone(Person.fields.occupation, { seq: 40, listColumn: false, fieldGroup: 'display' }),
    address_id: clone(Person.fields.address_id, { seq: 45, listColumn: false }),
    specialty: clone(Person.fields.specialty, { seq: 50, listColumn: false, fieldGroup: 'display' }),
    employment_grade: clone(Person.fields.employment_grade, { seq: 55, listColumn: false, fieldGroup: 'display' }),
    year_group: clone(Person.fields.year_group, { seq: 60, listColumn: false, fieldGroup: 'display' }),
    receive_event_reminders: clone(User.fields.receive_event_reminders, {
      seq: 65,
      listColumn: false,
    }),
    receive_unread_msg_emails: clone(User.fields.receive_unread_msg_emails, {
      seq: 70,
      listColumn: false,
    }),
    skip_all_email_sending: clone(User.fields.skip_all_email_sending, {
      seq: 75,
      listColumn: false,
    }),
    privacy_policy_accepted_version: clone(User.fields.privacy_policy_accepted_version, {
      seq: 80,
      listColumn: false,
    }),
    platform_terms_accepted_version: clone(User.fields.platform_terms_accepted_version, {
      seq: 85,
      listColumn: false,
    }),
    booking_cancellation_refund_terms_accepted_version: clone(
      User.fields.booking_cancellation_refund_terms_accepted_version,
      {
        seq: 90,
        listColumn: false,
      },
    ),
    host_payments_payouts_terms_accepted_version: clone(User.fields.host_payments_payouts_terms_accepted_version, {
      seq: 95,
      listColumn: false,
    }),
    unsuccessful_login_attempts: clone(User.fields.unsuccessful_login_attempts, { seq: 100, listColumn: false }),
    stripe_connect_account_id: clone(User.fields.stripe_connect_account_id, { seq: 105, listColumn: false }),
    stripe_connect_details_submitted: clone(User.fields.stripe_connect_details_submitted, {
      seq: 110,
      listColumn: false,
    }),
    stripe_connect_charges_enabled: clone(User.fields.stripe_connect_charges_enabled, { seq: 115, listColumn: false }),
    stripe_connect_payouts_enabled: clone(User.fields.stripe_connect_payouts_enabled, { seq: 120, listColumn: false }),
    stripe_connect_onboarded_at: clone(User.fields.stripe_connect_onboarded_at, { seq: 125, listColumn: false }),
    created_at: clone(User.fields.created_at, { seq: 130, listColumn: false }),
    person_id: clone(User.fields.person_id, { seq: 135, listColumn: false, filterable: false }),
    pwa_status: clone(User.fields.pwa_status, { seq: 140 }),
    pwa_installed_at: clone(User.fields.pwa_installed_at, { seq: 145, listColumn: false }),
  },
};

InstitutionMember.fields.user_id.type = FieldType.Reference;
(InstitutionMember.fields.user_id as ReferenceField).refEntity = UserView;
